import React, { ReactElement, useEffect, useRef, useState } from 'react'

import cx from 'classnames'

import { Flex, StatusBadge } from '@driftt/dds'
import { Button, Div } from '@driftt/tide-core'

import Card from 'components/Card'
import TooltipHoverPortal from 'components/TooltipHoverPortal'
import { useAppSelector } from 'hooks/useRedux'
import useSalesforceData from 'hooks/useSalesforceData'
import { ActivityGroupByEndUser, CompanyData } from 'modules/accountActivity/domain'
import { ReactComponent as LinkedInIcon } from 'svgs/linkedin.svg'
import { ReactComponent as SalesforceIcon } from 'svgs/salesforce.svg'
import { buildLinkedInSearchUrl } from 'utils/linkedin'

import getToolTipData from './getToolTipData'

import styles from './styles.module.css'

type ToolTipData = ActivityGroupByEndUser | CompanyData

interface ActivityContentToolTipProps {
  children: ReactElement | ReactElement[]
  className?: string
  data: ToolTipData
  side?: 'left' | 'right' | 'top' | 'bottom'
  inCrm?: boolean
}

interface ToolTipContentProps {
  data: ToolTipData
  inCrm?: boolean
}

const TooltipContent = ({ data, inCrm = true }: ToolTipContentProps) => {
  const orgUsersById = useAppSelector(({ orgUsersById }) => orgUsersById.users)
  const salesforceData = useSalesforceData(data)

  const { Avatar, avatarProps, avatarLabel, subheader, activityInfo } =
    getToolTipData(data, orgUsersById) || {}

  const onClickLinkedIn = () => {
    const endUserDataObj = data as ActivityGroupByEndUser
    const companyDataObj = data as CompanyData

    const endUser = endUserDataObj?.endUserData
    const companyData = endUser ? endUserDataObj.activityList[0].accountData : companyDataObj
    const linkedInUrl = buildLinkedInSearchUrl(endUser, companyData)
    window.open(linkedInUrl)
  }

  const onClickSalesforce = () => {
    if (!salesforceData) {
      return
    }
    const baseUrl = salesforceData.baseUrl
    const path = salesforceData.contacts.length
      ? salesforceData.contacts[0]?.Id
      : salesforceData.accounts[0]?.Id

    path && window.open(`${baseUrl}${path}`)
  }

  return (
    <Card className={styles.TooltipCard}>
      <div className={styles.TooltipHeader}>
        <Avatar className={styles.AvatarIcon} {...avatarProps} inCrm={inCrm} />
        <Flex direction="column">
          <div className={cx(styles.AvatarLabel, styles.TwoLineTruncated)}>{avatarLabel}</div>
          {inCrm === false && (
            <StatusBadge
              css={{
                marginLeft: '$1'
              }}
              variant="warning">
              Account record was not found in Drift CRM
            </StatusBadge>
          )}
        </Flex>
      </div>

      {subheader}

      {activityInfo && <Div className={styles.ActivityInfoContainer}>{activityInfo}</Div>}

      <div>
        <Button className={styles.ActionButton} type="utility" onClick={onClickLinkedIn}>
          <LinkedInIcon />
        </Button>
        {salesforceData && (
          <Button className={styles.ActionButton} type="utility" onClick={onClickSalesforce}>
            <SalesforceIcon />
          </Button>
        )}
      </div>
    </Card>
  )
}

const ActivityContentToolTip = ({
  children,
  className,
  data,
  side = 'right',
  inCrm = true
}: ActivityContentToolTipProps): ReactElement | any => {
  const toolTipRef = useRef(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (toolTipRef.current && !ready) {
      setReady(true)
    }
  }, [toolTipRef, ready, setReady])

  return (
    <TooltipHoverPortal
      className={cx(styles.TooltipContainer, className)}
      boundaryRef={toolTipRef.current}
      refElement={toolTipRef.current}
      placement={side}
      tooltipBody={<TooltipContent data={data} inCrm={inCrm} />}>
      <span className={styles.TooltipContentContainer} ref={toolTipRef}>
        {children}
      </span>
    </TooltipHoverPortal>
  )
}

export default ActivityContentToolTip
